import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  FlaskConical,
  Loader2,
  Pill,
  Plus,
  ScanLine,
  X,
} from 'lucide-react';
import { aiGatewayService } from '../services/aiGateway';
import { Button } from '@/components/ui/button';
import type { RxInboxItem } from '../types/attachmentClassifier';

/**
 * Per-item proposal card for AI-suggested lab orders / imaging /
 * prescriptions. Extracted from AIAssistantBubble so the /assistant
 * workspace renders the same confirm/undo flow as the bubble — one
 * implementation, two surfaces. Confirm round-trips over
 * `scribe.confirm-proposal`; prescriptions prefill the Rx form as a
 * batch (nothing auto-created server-side).
 */
type ProposalKind = 'lab' | 'imaging' | 'prescription';

interface ProposalCardProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  action: any; // AIAction with type=propose_* and parameters={patientId, patientName, items[]}
  onDismiss: () => void;
}

interface ProposalItemState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item: any;
  status: 'pending' | 'adding' | 'added' | 'skipped' | 'failed';
  message?: string;
  // Server id of the created order — captured from
  // scribe.confirm-proposal-result so Undo can cancel it.
  entityId?: string;
}

export const kindFromActionType = (t: string): ProposalKind | null => {
  if (t === 'propose_lab_orders') return 'lab';
  if (t === 'propose_imaging_orders') return 'imaging';
  if (t === 'propose_prescriptions') return 'prescription';
  return null;
};

// Map one proposed-prescription item ({medication, dosage, frequency,
// duration, route}) to the form's prefill shape. Mirrors the backend
// `handleConfirmProposal` mapping so the prefilled form matches what the
// per-item navigate used to produce — only now ALL proposed meds are
// carried through at once instead of clobbering each other.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const proposalToRxInbox = (item: any): RxInboxItem => {
  const durationStr = String(item?.duration ?? '');
  const durMatch = durationStr.match(/(\d+)/);
  return {
    medicationName: String(
      item?.recommendation?.medication ?? item?.medication ?? '',
    ).trim(),
    dose: item?.dosage != null ? String(item.dosage) : null,
    doseUnit: null,
    frequency: item?.frequency != null ? String(item.frequency) : null,
    durationDays: durMatch ? parseInt(durMatch[1], 10) : null,
    quantity: null,
    refillsAuthorized: null,
    route: item?.route != null ? String(item.route) : null,
    diagnosis: null,
    notes: null,
    confidence: 1,
  };
};

export const ProposalCard: React.FC<ProposalCardProps> = ({ action, onDismiss }) => {
  const navigate = useNavigate();
  const kind = kindFromActionType(action?.type ?? '') ?? 'lab';
  const patientId = action?.parameters?.patientId as string | undefined;
  const patientName = action?.parameters?.patientName as string | undefined;
  const initialItems = React.useMemo<ProposalItemState[]>(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (action?.parameters?.items ?? []).map((it: any) => ({
        item: it,
        status: 'pending' as const,
      })),
    [action],
  );
  const [items, setItems] = useState<ProposalItemState[]>(initialItems);
  const [undone, setUndone] = useState(false);

  // Map FE-temp ids to indices for the WS round-trip.
  const actionId = React.useMemo(
    () =>
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `prop-${Date.now()}`,
    [],
  );

  // Live ref so the create/undo helpers read current item state without
  // being re-created on every status change.
  const itemsRef = React.useRef<ProposalItemState[]>(items);
  React.useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const createItem = React.useCallback(
    (idx: number) => {
      const target = itemsRef.current[idx];
      if (!target) return;
      // No patient linked → the order CANNOT be placed. Surface it as a
      // visible failure instead of silently doing nothing — otherwise the
      // card looks placed and the assistant's "ordered" reads as a lie.
      if (!patientId) {
        setItems((prev) =>
          prev.map((it, i) =>
            i === idx
              ? {
                  ...it,
                  status: 'failed',
                  message:
                    'Not placed — no patient linked to this chat. Attach the patient, then order again.',
                }
              : it,
          ),
        );
        return;
      }
      setItems((prev) =>
        prev.map((it, i) => (i === idx ? { ...it, status: 'adding' } : it)),
      );
      aiGatewayService.scribeConfirmProposal({
        kind,
        patientId,
        patientName,
        item: target.item,
        itemIndex: idx,
        actionId,
      });
    },
    [patientId, patientName, kind, actionId],
  );

  // Create result.
  React.useEffect(() => {
    const off = aiGatewayService.on(
      'scribe.confirm-proposal-result',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (payload: any) => {
        if (!payload || payload.actionId !== actionId) return;
        const idx = payload.itemIndex as number | undefined;
        if (idx == null) return;
        setItems((prev) =>
          prev.map((it, i) =>
            i === idx
              ? {
                  ...it,
                  status: payload.ok ? 'added' : 'failed',
                  entityId: payload.entityId ?? it.entityId,
                  message: payload.summary ?? payload.error,
                }
              : it,
          ),
        );
        if (payload.ok && payload.navigateTo) {
          // Prescription path — backend returns a navigation hint to the
          // prefilled Rx form (nothing auto-created, so nothing to undo).
          try {
            navigate(payload.navigateTo, { state: payload.navigateState });
          } catch {
            /* ignore — bubble lives at app root; fall back to no-op */
          }
        }
      },
    );
    return () => off?.();
  }, [actionId, navigate]);

  // Undo result.
  React.useEffect(() => {
    const off = aiGatewayService.on(
      'scribe.cancel-proposal-result',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (payload: any) => {
        if (!payload || payload.actionId !== actionId) return;
        const idx = payload.itemIndex as number | undefined;
        if (idx == null) return;
        setItems((prev) =>
          prev.map((it, i) =>
            i === idx ? { ...it, status: payload.ok ? 'skipped' : it.status } : it,
          ),
        );
      },
    );
    return () => off?.();
  }, [actionId]);

  // Prescriptions are never auto-created server-side (too many required
  // fields); they prefill the Rx form instead. Carry ALL proposed meds
  // through in ONE navigation as a batch, so the doctor reviews them as a
  // single session — the old per-item path fired a navigate per med, each
  // clobbering the previous, dropping every med but one.
  const placePrescriptionBatch = React.useCallback(() => {
    if (!patientId) {
      setItems((prev) =>
        prev.map((it) => ({
          ...it,
          status: 'failed' as const,
          message:
            'Not placed — no patient linked to this chat. Attach the patient, then order again.',
        })),
      );
      return;
    }
    const rxItems = itemsRef.current
      .map((it) => proposalToRxInbox(it.item))
      .filter((it) => it.medicationName.trim().length > 0);
    if (rxItems.length === 0) return;
    try {
      sessionStorage.setItem(
        'rx-prefill-queue',
        JSON.stringify({ patientId, items: rxItems }),
      );
    } catch {
      /* sessionStorage unavailable (incognito) — fall through to navigate */
    }
    setItems((prev) => prev.map((it) => ({ ...it, status: 'added' as const })));
    try {
      navigate('/prescriptions/new', { state: { patientId } });
    } catch {
      /* bubble lives at app root; ignore navigation failures */
    }
  }, [patientId, navigate]);

  // AUTO-EXECUTE: the model only emits this proposal AFTER the physician
  // verbally confirmed (see patient-qa.prompt.ts), so place every item
  // immediately — no buttons. Runs once on mount.
  const startedRef = React.useRef(false);
  React.useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    if (kind === 'prescription') {
      placePrescriptionBatch();
      return;
    }
    itemsRef.current.forEach((it, idx) => {
      if (it.status === 'pending') createItem(idx);
    });
  }, [createItem, kind, placePrescriptionBatch]);

  const handleUndo = () => {
    setUndone(true);
    itemsRef.current.forEach((it, idx) => {
      if (it.status === 'added' && it.entityId) {
        aiGatewayService.scribeCancelProposal({
          kind,
          entityId: it.entityId,
          itemIndex: idx,
          actionId,
        });
      }
    });
  };

  const anyAdding = items.some((it) => it.status === 'adding');
  const settled = items.length > 0 && !anyAdding;
  const anyFailed = items.some((it) => it.status === 'failed');
  const allFailed = items.length > 0 && items.every((it) => it.status === 'failed');
  // Undo only applies to things actually created server-side (lab/imaging
  // carry an entityId; Rx navigates to a form, nothing to cancel).
  const undoable = items.some((it) => it.status === 'added' && it.entityId);

  // Auto-dismiss once settled, leaving a window to hit Undo — but KEEP a
  // failed card up (don't quietly vanish a "not placed" the doctor needs
  // to see, or the assistant's "ordered" reads as a silent lie).
  React.useEffect(() => {
    if (settled && !anyFailed) {
      const t = setTimeout(onDismiss, undone ? 1500 : 8000);
      return () => clearTimeout(t);
    }
  }, [settled, anyFailed, undone, onDismiss]);

  const kindLabel =
    kind === 'lab' ? 'Lab orders' : kind === 'imaging' ? 'Imaging' : 'Prescriptions';
  const KindIcon = kind === 'lab' ? FlaskConical : kind === 'imaging' ? ScanLine : Pill;

  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        <KindIcon className="w-5 h-5" style={{ color: 'var(--color-primary-600)' }} />
        <h4 className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
          {anyAdding
            ? `Ordering ${kindLabel.toLowerCase()}…`
            : allFailed
              ? `Couldn't place ${kindLabel.toLowerCase()}`
              : `${kindLabel} placed${anyFailed ? ' (some failed)' : ''}`}
          {patientName ? ` for ${patientName}` : ''}
        </h4>
      </div>
      <div className="space-y-2 mb-3">
        {items.map((it, idx) => (
          <ProposalItemRow key={idx} kind={kind} state={it} autoMode />
        ))}
      </div>
      {settled && undoable && !undone && (
        <Button variant="secondary" onClick={handleUndo} className="w-full">
          Undo
        </Button>
      )}
      {undone && (
        <div className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
          Undone — orders cancelled.
        </div>
      )}
    </>
  );
};

interface ProposalItemRowProps {
  kind: ProposalKind;
  state: ProposalItemState;
  onAdd?: () => void;
  onSkip?: () => void;
  // Conversational flow: orders auto-place after the verbal confirmation,
  // so there are no per-item buttons — pending items show a spinner.
  autoMode?: boolean;
}

const ProposalItemRow: React.FC<ProposalItemRowProps> = ({
  kind,
  state,
  onAdd,
  onSkip,
  autoMode = false,
}) => {
  const item = state.item ?? {};
  const title =
    kind === 'lab'
      ? String(item.testName ?? 'Unnamed test')
      : kind === 'imaging'
        ? `${String(item.modality ?? 'Imaging')} ${String(item.bodyPart ?? '')}`.trim()
        : String(item.medication ?? 'Unnamed medication');
  const subtitle =
    kind === 'lab'
      ? [item.priority, item.notes].filter(Boolean).join(' · ')
      : kind === 'imaging'
        ? [item.indication, item.priority].filter(Boolean).join(' · ')
        : [item.dosage, item.frequency, item.duration]
            .filter(Boolean)
            .join(' · ');

  const isAdding = state.status === 'adding';
  const isAdded = state.status === 'added';
  const isSkipped = state.status === 'skipped';
  const isFailed = state.status === 'failed';

  return (
    <div
      className="flex items-center gap-2 p-2 rounded-lg border"
      style={{
        backgroundColor: 'var(--color-bg-primary)',
        borderColor: isFailed
          ? 'var(--color-error)'
          : isAdded
            ? 'color-mix(in srgb, var(--color-success) 40%, var(--color-border-primary))'
            : 'var(--color-border-primary)',
        opacity: isSkipped ? 0.55 : 1,
      }}
    >
      <div className="flex-1 min-w-0">
        <div
          className="text-sm font-medium truncate"
          style={{
            color: 'var(--color-text-primary)',
            textDecoration: isSkipped ? 'line-through' : 'none',
          }}
        >
          {title}
        </div>
        {subtitle && (
          <div
            className="text-xs truncate"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            {subtitle}
          </div>
        )}
        {state.message && (
          <div
            className="text-xs mt-0.5"
            style={{
              color: isFailed ? 'var(--color-error)' : 'var(--color-success)',
            }}
          >
            {state.message}
          </div>
        )}
      </div>
      {isAdded ? (
        <CheckCircle
          className="w-5 h-5 flex-shrink-0"
          style={{ color: 'var(--color-success)' }}
        />
      ) : isAdding ? (
        <Loader2
          className="w-4 h-4 animate-spin flex-shrink-0"
          style={{ color: 'var(--color-text-tertiary)' }}
        />
      ) : isSkipped ? (
        <span
          className="text-[11px] uppercase tracking-wide flex-shrink-0"
          style={{ color: 'var(--color-text-tertiary)' }}
        >
          Skipped
        </span>
      ) : autoMode ? (
        <Loader2
          className="w-4 h-4 animate-spin flex-shrink-0"
          style={{ color: 'var(--color-text-tertiary)' }}
        />
      ) : (
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            variant="primary"
            size="xs"
            onClick={onAdd}
            title="Add to patient"
          >
            <Plus className="w-3 h-3" />
            <span className="ms-1">Add</span>
          </Button>
          <Button
            variant="ghost"
            size="xs"
            onClick={onSkip}
            title="Skip this item"
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
};
